import React, {useEffect, useContext} from 'react'
import RegisterButton from './registerButton';
import {isRegistrationOpenByTimestamps} from '../helpers/coursesHelper';
import LoadingPanel from './loadingPanel';

// import api
import { useGetFutureCoursesInstanceDetailsQuery } from '../api/courses';
// import context
import { Context } from '../courseTemplates';

/**
 * Expanded accordion future course instance data
 * @param {Object} instanceId - Future course instance id
 * @param {Object} courseRecordId - Course record id
 * @param {Function} futureCoursesRefetch - Refetch future courses for this course record id - used on accordion expand
 */
function FutureCourseInstance({instanceId, courseRecordId, futureCoursesRefetch}) {
    // get our global context variables
    const value = useContext(Context);
    const { customerId } = value;

    // get the future courses with the new RTK query
    // Using a query hook automatically fetches data and returns query values
   const { data: instanceData, error: instanceError, currentData: instanceCurrentData, isFetching: instanceIsFetching } = useGetFutureCoursesInstanceDetailsQuery({courseRecordId, instanceId});

   // currency formatter
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        // These options are needed to round to whole numbers if that's what you want.
        // minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
        maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
    });

    /**
     * Get center column data
     * @param {Object} catDetails - Details for the current category row
     * @returns JSX
     */
    const getCenterColumn = (catDetails) => {
        // if user is enrolled and sitting in the conference room
        if (catDetails.enrolled && catDetails.sittingInConferenceRoom){
            return(
                <em>Sitting in {catDetails.seat.classroom }</em>
            )
        // if user is enrolled and have a seat
        } else if (catDetails.enrolled && catDetails.seat) {
            return(
                <>
                    <h5 className="cell-headline">{ catDetails.seat.classroom }</h5>
                    <div>Seat {catDetails.seat.seatNumber} | <a href="#" className="seating-popup" data-customerid={customerId} data-courseid={instanceData.course_record_id} data-categoryid={catDetails.enrollmentCategoryRecordId}>View</a></div>
                </>
            )
        // if user is not enrolled and not waitlisted and they have seat data to display
        } else if (!isRegistrationOpenByTimestamps(instanceData.course_details.registrationOpensDate, instanceData.course_details.registrationClosesDate)){
            return(<></>)
        }  else if (!catDetails.enrolled && !catDetails.waitlisted && catDetails.seatsData){
            return(
            <>{catDetails.seatsData.message }</>
            )
        } 
    }

    useEffect(() => {
        futureCoursesRefetch({courseRecordId, sections: 'future'});
    }, [instanceCurrentData]);

    /**
     * Loops through categories and returns JSX containing all the rows
     * @returns JSX
     */
    const getCategoryRows = () => {
        // if we have data returned from method
        if (instanceData && instanceData.course_details.catDetails) {
            return (
                // loop through the categories and return a row for each
                Object.keys(instanceData.course_details.catDetails).map((key) => {
                    return(
                        <div className="table-course-list__row responsive-grid rg-align-items-center">
                            <div className="rg-5 rg-sm-12 rg-xs-12">
                                <h4 className="cell-headline">{instanceData.course_details.catDetails[key].catName} Signup</h4>
                                {formatter.format(Number(instanceData.course_details.catDetails[key].price.price))}
                            </div>
                            <div className="rg-3 rg-sm-12 rg-xs-12">
                                {getCenterColumn(instanceData.course_details.catDetails[key])}
                            </div>
                            <div className="rg-4 rg-sm-12 rg-xs-12">
                                <RegisterButton 
                                    catDetails={instanceData.course_details.catDetails[key]} 
                                    courseDataJWT={instanceData.courseDataJWT} 
                                    course_record_id={instanceData.course_record_id}
                                    courseProduct={instanceData.course_product}
                                    membershipPolicyPageLink={instanceData.membership_policy_page_link}
                                    classDate={instanceData.course_details.classDate}
                                    restrictedEnrollmentCategories={instanceData.restricted_enrollment_categories}
                                    templateId={courseRecordId}
                                />
                            </div>
                        </div>
                    )
                })
            );
        } else if (instanceData && !instanceData.course_details.canTakeCourse && instanceData.course_details.canTakeCourseMessage) {
            return (
                <div className="table-course-list__row responsive-grid">
                    <div className="rg-12 rg-sm-12 rg-xs-12">
                        {instanceData.course_details.canTakeCourseMessage}
                    </div>
                </div>
            )
        }
    };

    // Component level JSX return statement ******************************************************
    return (
        <>
        {/* if we dont have data and its fetching show the loading panel */}
        {!instanceCurrentData &&
            <LoadingPanel loadText="Loading Course Details"/>
        }
        {/* if we have data and it's not fetching */}
            {instanceCurrentData &&
            getCategoryRows()}
        </>
    );
}

export default FutureCourseInstance;